<script setup lang="ts">
import { computed } from 'vue';

import { useToast } from '../composables/useToast';
import type { GameEntity } from '../types';

const { showToast, ToastComponent } = useToast();

const props = defineProps<{
  entity: GameEntity;
  entities: GameEntity[]; // All entities in the world
  tick: number | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
}>();

/**
 * Resolves an entity ID to a displayable item name and type.
 * Uses the entities list to find DisplayName, Item, and Stack components.
 */
function resolveItem(itemId: number | null) {
  if (itemId === null || itemId === undefined) return null;

  const itemEntity = props.entities.find((e) => e.id === itemId);
  if (!itemEntity) return { id: itemId, name: 'Unknown', type: 'Unknown' };

  const nameComp = itemEntity.components.DisplayName;
  const itemComp = itemEntity.components.Item;
  const stackComp = itemEntity.components.Stack;

  // Prioritize DisplayName, then ItemRegistry ID, then generic Entity ID
  let text = nameComp?.name || itemComp?.itemId || `Entity ${itemId}`;
  if (stackComp && stackComp.quantity > 1) {
    text += ` (x${stackComp.quantity})`;
  }

  return {
    id: itemId,
    name: text,
    type: itemEntity.type,
  };
}

/**
 * Resolved list of inventory items.
 */
const inventory = computed(() => {
  const inv = props.entity.components.Inventory;
  if (!inv || !inv.slots) return null;
  return inv.slots.map(resolveItem);
});

/**
 * Resolved map of equipment slots.
 */
const equipment = computed(() => {
  const equip = props.entity.components.Equipment;
  if (!equip || !equip.slots) return null;

  const result: Record<string, ReturnType<typeof resolveItem>> = {};
  for (const [slot, itemId] of Object.entries(equip.slots)) {
    result[slot] = resolveItem(itemId as number | null);
  }
  return result;
});

function copyToClipboard() {
  const data = JSON.stringify(props.entity.components, null, 2);

  if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') {
    showToast('Clipboard API is not available in this browser. Please copy the data manually.', 'error');
    return;
  }

  navigator.clipboard
    .writeText(data)
    .then(() => {
      showToast('Entity data copied to clipboard!', 'success');
    })
    .catch((err) => {
      console.error('Failed to copy entity data to clipboard:', err);
      showToast('Failed to copy entity data to clipboard. Please copy it manually.', 'error');
    });
}
</script>

<template>
  <div class="entity-detail">
    <ToastComponent />
    <div class="header">
      <div class="title-row">
        <h3>Entity ID: {{ entity.id }} ({{ entity.type }})</h3>
        <span
          v-if="tick !== null"
          class="tick-badge"
        >
          <span class="live-dot" />
          Tick {{ tick }}
        </span>
      </div>
      <div class="actions">
        <button
          class="btn btn-secondary"
          @click="copyToClipboard"
        >
          📋 Copy to Clipboard
        </button>
        <button
          class="btn"
          @click="emit('back')"
        >
          Back to List
        </button>
      </div>
    </div>

    <!-- Inventory Panel -->
    <div
      v-if="inventory"
      class="panel inventory-panel"
    >
      <div class="panel-header">
        Inventory
      </div>
      <div class="inventory-grid">
        <div
          v-for="(item, index) in inventory"
          :key="index"
          class="inventory-slot"
        >
          <div class="slot-number">
            {{ index }}
          </div>
          <div
            v-if="item"
            class="slot-content"
          >
            <span class="item-name">{{ item.name }}</span>
            <span class="item-id">#{{ item.id }} ({{ item.type }})</span>
          </div>
          <div
            v-else
            class="slot-empty"
          >
            Empty
          </div>
        </div>
      </div>
    </div>

    <!-- Equipment Panel -->
    <div
      v-if="equipment"
      class="panel equipment-panel"
    >
      <div class="panel-header">
        Equipment
      </div>
      <div class="equipment-list">
        <div
          v-for="(item, slot) in equipment"
          :key="slot"
          class="equipment-row"
        >
          <div class="slot-name">
            {{ slot }}
          </div>
          <div
            v-if="item"
            class="slot-content"
          >
            <span class="item-name">{{ item.name }}</span>
            <span class="item-id">#{{ item.id }} ({{ item.type }})</span>
          </div>
          <div
            v-else
            class="slot-empty"
          >
            Empty
          </div>
        </div>
      </div>
    </div>

    <div class="panel">
      <div class="panel-header">
        Raw Components
      </div>
      <div
        v-for="(data, name) in entity.components"
        :key="name"
        class="component-card"
      >
        <div class="component-header">
          {{ name }}
        </div>
        <pre class="component-data">{{ JSON.stringify(data, null, 2) }}</pre>
      </div>
    </div>
  </div>
</template>

<style scoped>
.entity-detail {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.actions {
  display: flex;
  gap: 10px;
}

.btn-secondary {
  background: #444;
  border-color: #555;
}

.btn-secondary:hover {
  background: #555;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.tick-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #1a3a1a;
  border: 1px solid #2a5a2a;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 0.85em;
  color: #8f8;
}

.live-dot {
  width: 8px;
  height: 8px;
  background: #4f4;
  border-radius: 50%;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

.panel {
  background: #1e1e1e;
  border-radius: 8px;
  border: 1px solid #333;
  overflow: hidden;
}

.panel-header {
  background: #2d2d2d;
  padding: 8px 15px;
  font-weight: bold;
  border-bottom: 1px solid #333;
  color: #aaa;
  text-transform: uppercase;
  font-size: 0.8em;
  letter-spacing: 0.05em;
}

.inventory-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1px;
  background: #333;
}

.inventory-slot {
  background: #1e1e1e;
  padding: 10px;
  display: flex;
  gap: 10px;
  align-items: center;
}

.slot-number {
  color: #555;
  font-family: monospace;
  font-size: 0.8em;
  min-width: 15px;
}

.slot-content {
  display: flex;
  flex-direction: column;
}

.item-name {
  color: #fff;
  font-weight: 500;
}

.item-id {
  color: #777;
  font-size: 0.75em;
}

.slot-empty {
  color: #444;
  font-style: italic;
  font-size: 0.9em;
}

.equipment-list {
  display: flex;
  flex-direction: column;
  gap: 1px;
  background: #333;
}

.equipment-row {
  background: #1e1e1e;
  padding: 10px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.slot-name {
  color: #aaa;
  font-weight: bold;
  font-size: 0.85em;
  text-transform: capitalize;
}

.component-card {
  border-bottom: 1px solid #333;
}

.component-card:last-child {
  border-bottom: none;
}

.component-header {
  background: #252525;
  padding: 5px 15px;
  font-weight: bold;
  font-size: 0.85em;
  color: #888;
}

.component-data {
  padding: 10px 15px;
  margin: 0;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.8em;
  color: #bbb;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>



